import { z } from 'zod';
/**
 * THE free-text policy (handoff 0009 §1): one base layer every free-text value gets, plus
 * profiles that add character rules by what the text IS. The API is authoritative; the web
 * admin forms get it for free through the contract schemas; the Flutter kiosk ports it
 * (`apps/kiosk`) and both implementations must pass `fixtures/text-policy-vectors.json`.
 *
 * ## Profiles
 *
 * - `personName`    — letters, marks, space, `-` `'` `’` `ʼ` `.` only; ≥1 letter. First/last names.
 * - `label`         — letters, marks, numbers, punctuation, space and `& + @ # $ € £ ¥ % / ( ) ® ™ ©`.
 * - `freeText`      — `label`'s character set; `multiLine` also permits `\n`.
 * - `displayCopy`   — base rules 1–7 only: emoji ALLOWED, no character set. Admin-authored copy.
 * - `searchText`    — NFC + trim only. NEVER rejects.
 * - `kioskFullName` — the kiosk's single `visitor_name` field (§1d): `personName` rules per word,
 *                     ≤201 code points, and it must name both a first and a last name.
 *
 * ## Normalisation (always, before any check; the normalised form is what is stored)
 *
 *   N1. NFC.
 *   N2. multiLine only: `\r\n` and lone `\r` → `\n`.
 *   N3. Trim leading/trailing `[\p{Zs}\t\n\v\f\r]` (an explicit set — NOT the host language's
 *       `trim()`, whose whitespace sets differ between JS and Dart and include U+FEFF).
 *   N4. Collapse every run of `\p{Zs}` (space, NBSP, U+3000 …) to ONE U+0020.
 *       Tabs/newlines are NOT collapsed — they are control characters and are rejected below.
 *   N5. multiLine only: strip U+0020 at the start and end of every line, then collapse 4+
 *       consecutive `\n` to exactly 3.
 *   `searchText` stops after N1 + N3.
 *
 * ## Check order — FIXED. The Dart port must replicate it exactly: the first failing check wins.
 *
 *   C1. required      — empty after normalisation → TEXT_REQUIRED (`allowEmpty` → ok `''`).
 *   C2. control       — any U+0000–001F, U+007F–009F (`\p{Cc}`), U+2028, U+2029 →
 *                       TEXT_INVALID_CHARS. `\n` is exempt when `multiLine`.
 *   C3. invisible     — U+202A–202E, U+2066–2069, U+200B, U+2060, U+FEFF, U+00AD → TEXT_INVALID_CHARS.
 *   C4. emoji         — `\p{Extended_Pictographic}` EXCEPT © U+00A9, ® U+00AE, ™ U+2122 (label
 *                       symbols); regional indicators U+1F1E6–1F1FF; U+FE0F; keycap U+20E3; skin
 *                       tones U+1F3FB–1F3FF; tag characters U+E0020–E007F → TEXT_EMOJI_NOT_ALLOWED.
 *                       Skipped for `displayCopy`. (Before C5 so an emoji ZWJ sequence reports
 *                       the emoji, not the joiner.)
 *   C5. joiner        — ZWNJ U+200C / ZWJ U+200D must sit between two letters → else
 *                       TEXT_INVALID_CHARS. "Before" is a letter, or a run of marks whose base
 *                       is a letter (Indic: consonant + virama + ZWJ); "after" is a letter.
 *                       `displayCopy` also allows ZWJ between two emoji (👩‍💻).
 *   C6. zalgo         — more than 3 consecutive `\p{M}` → TEXT_INVALID_CHARS.
 *   C7. character set — personName / kioskFullName: outside the name set → NAME_INVALID;
 *                       label / freeText: outside the label set → TEXT_INVALID_CHARS.
 *   C8. letter        — personName: no `\p{L}` at all → NAME_INVALID. kioskFullName: any
 *                       space-separated word without a `\p{L}` → NAME_INVALID.
 *   C9. length        — code points (never UTF-16 units) > `max` → TEXT_TOO_LONG.
 *   C10. kioskFullName only: fewer than 2 words, unless ONE word of ≥2 code points containing a
 *                       Han/Hiragana/Katakana/Hangul character → NAME_NEEDS_FIRST_AND_LAST.
 *   C11. kioskFullName only: either part of `splitFullName` > 100 → TEXT_TOO_LONG.
 *
 * Every regular expression here is a single character class tested against ONE code point, or a
 * `+` over a single class with nothing after it that can backtrack — linear time. That is why the
 * API may run them server-side, unlike a tenant-authored `validation.pattern`.
 *
 * Validation is NOT the XSS control (React's output escaping is). Never validate on read.
 */
export const TEXT_POLICY_CODES = [
    'TEXT_REQUIRED',
    'TEXT_TOO_LONG',
    'TEXT_EMOJI_NOT_ALLOWED',
    'TEXT_INVALID_CHARS',
    'NAME_INVALID',
    'NAME_NEEDS_FIRST_AND_LAST',
];
export function isTextPolicyCode(value) {
    return TEXT_POLICY_CODES.includes(value);
}
export const TEXT_PROFILES = [
    'personName',
    'label',
    'freeText',
    'displayCopy',
    'searchText',
    'kioskFullName',
];
/** One part of a person's name — first or last (§1b). */
export const PERSON_NAME_PART_MAX_LENGTH = 100;
/** The kiosk's single full-name field: 100 + space + 100 (§1d). */
export const KIOSK_FULL_NAME_MAX_LENGTH = 201;
/* -------------------------------------------------------------------------- */
/* Code-point classifiers (each tests exactly one code point — linear)         */
/* -------------------------------------------------------------------------- */
const RE_LETTER = /^\p{L}$/u;
const RE_MARK = /^\p{M}$/u;
const RE_NUMBER = /^\p{N}$/u;
const RE_PUNCT = /^\p{P}$/u;
const RE_SPACE_SEPARATOR = /^\p{Zs}$/u;
const RE_PICTOGRAPHIC = /^\p{Extended_Pictographic}$/u;
const RE_SPACELESS_SCRIPT = /^[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]$/u;
const RE_SPACE_RUN = /\p{Zs}+/gu;
const RE_CR = /\r\n?/g;
const RE_NEWLINE_RUN = /\n{4,}/g;
const SPACE = 0x20;
const NEWLINE = 0x0a;
const ZWNJ = 0x200c;
const ZWJ = 0x200d;
/** `& + @ # $ € £ ¥ % / ( ) ® ™ ©` — the only symbols `label`/`freeText` accept beyond `\p{P}`. */
const LABEL_SYMBOLS = new Set(Array.from('&+@#$€£¥%/()®™©', (ch) => ch.codePointAt(0) ?? 0));
/** `-` `'` `’` `ʼ` `.` — the only non-letter, non-mark, non-space characters a name accepts. */
const NAME_PUNCTUATION = new Set([0x2d, 0x27, 0x2019, 0x02bc, 0x2e]);
/** Label symbols that are also `\p{Extended_Pictographic}` and must not trip the emoji check. */
const PICTOGRAPHIC_LABEL_SYMBOLS = new Set([0x00a9, 0x00ae, 0x2122]);
function charOf(cp) {
    return String.fromCodePoint(cp);
}
const isLetter = (cp) => RE_LETTER.test(charOf(cp));
const isMark = (cp) => RE_MARK.test(charOf(cp));
const isNumber = (cp) => RE_NUMBER.test(charOf(cp));
const isPunct = (cp) => RE_PUNCT.test(charOf(cp));
const isSpacelessScript = (cp) => RE_SPACELESS_SCRIPT.test(charOf(cp));
function isTrimmable(cp) {
    return (cp >= 0x09 && cp <= 0x0d) || RE_SPACE_SEPARATOR.test(charOf(cp));
}
function isControl(cp, multiLine) {
    if (cp === NEWLINE && multiLine)
        return false;
    return cp <= 0x1f || (cp >= 0x7f && cp <= 0x9f) || cp === 0x2028 || cp === 0x2029;
}
function isInvisible(cp) {
    return ((cp >= 0x202a && cp <= 0x202e) ||
        (cp >= 0x2066 && cp <= 0x2069) ||
        cp === 0x200b ||
        cp === 0x2060 ||
        cp === 0xfeff ||
        cp === 0x00ad);
}
function isEmoji(cp) {
    if (PICTOGRAPHIC_LABEL_SYMBOLS.has(cp))
        return false;
    return ((cp >= 0x1f1e6 && cp <= 0x1f1ff) ||
        cp === 0xfe0f ||
        cp === 0x20e3 ||
        (cp >= 0x1f3fb && cp <= 0x1f3ff) ||
        (cp >= 0xe0020 && cp <= 0xe007f) ||
        RE_PICTOGRAPHIC.test(charOf(cp)));
}
/** An emoji piece that may precede a ZWJ inside an emoji sequence (displayCopy only). */
function isEmojiSequencePart(cp) {
    return RE_PICTOGRAPHIC.test(charOf(cp)) || cp === 0xfe0f || (cp >= 0x1f3fb && cp <= 0x1f3ff);
}
function codePoints(value) {
    return Array.from(value, (ch) => ch.codePointAt(0) ?? 0);
}
/* -------------------------------------------------------------------------- */
/* Normalisation                                                              */
/* -------------------------------------------------------------------------- */
function trimEdges(value) {
    const chars = Array.from(value);
    const trimmable = (ch) => ch !== undefined && isTrimmable(ch.codePointAt(0) ?? 0);
    let start = 0;
    let end = chars.length;
    while (start < end && trimmable(chars[start]))
        start += 1;
    while (end > start && trimmable(chars[end - 1]))
        end -= 1;
    return chars.slice(start, end).join('');
}
function stripLineSpaces(line) {
    let start = 0;
    let end = line.length;
    while (start < end && line.charCodeAt(start) === SPACE)
        start += 1;
    while (end > start && line.charCodeAt(end - 1) === SPACE)
        end -= 1;
    return line.slice(start, end);
}
/** Steps N1–N5 above. `searchText` uses N1 + N3 only. */
export function normalizeText(value, profile, multiLine = false) {
    const nfc = value.normalize('NFC');
    if (profile === 'searchText')
        return trimEdges(nfc);
    const lines = multiLine && allowsMultiLine(profile);
    let v = lines ? nfc.replace(RE_CR, '\n') : nfc;
    v = trimEdges(v);
    v = v.replace(RE_SPACE_RUN, ' ');
    if (lines) {
        v = v.split('\n').map(stripLineSpaces).join('\n').replace(RE_NEWLINE_RUN, '\n\n\n');
    }
    return v;
}
function allowsMultiLine(profile) {
    return profile === 'freeText' || profile === 'displayCopy';
}
/* -------------------------------------------------------------------------- */
/* The checks                                                                 */
/* -------------------------------------------------------------------------- */
function letterBefore(cps, index) {
    let i = index - 1;
    while (i >= 0 && isMark(cps[i] ?? 0))
        i -= 1;
    return i >= 0 && isLetter(cps[i] ?? 0);
}
function joinerAllowed(cps, index, profile) {
    const next = cps[index + 1];
    if (next === undefined || index === 0)
        return false;
    if (isLetter(next) && letterBefore(cps, index))
        return true;
    return (profile === 'displayCopy' &&
        cps[index] === ZWJ &&
        isEmojiSequencePart(cps[index - 1] ?? 0) &&
        RE_PICTOGRAPHIC.test(charOf(next)));
}
function isNameChar(cp) {
    return (cp === SPACE ||
        cp === ZWNJ ||
        cp === ZWJ ||
        NAME_PUNCTUATION.has(cp) ||
        isLetter(cp) ||
        isMark(cp));
}
function isLabelChar(cp, multiLine) {
    return (cp === SPACE ||
        (cp === NEWLINE && multiLine) ||
        cp === ZWNJ ||
        cp === ZWJ ||
        LABEL_SYMBOLS.has(cp) ||
        isLetter(cp) ||
        isMark(cp) ||
        isNumber(cp) ||
        isPunct(cp));
}
/** C2–C6: the base layer every profile except `searchText` gets. */
function checkBase(cps, profile, multiLine) {
    if (cps.some((cp) => isControl(cp, multiLine)))
        return 'TEXT_INVALID_CHARS';
    if (cps.some(isInvisible))
        return 'TEXT_INVALID_CHARS';
    if (profile !== 'displayCopy' && cps.some(isEmoji))
        return 'TEXT_EMOJI_NOT_ALLOWED';
    for (let i = 0; i < cps.length; i += 1) {
        const cp = cps[i];
        if ((cp === ZWNJ || cp === ZWJ) && !joinerAllowed(cps, i, profile))
            return 'TEXT_INVALID_CHARS';
    }
    let markRun = 0;
    for (const cp of cps) {
        markRun = isMark(cp) ? markRun + 1 : 0;
        if (markRun > 3)
            return 'TEXT_INVALID_CHARS';
    }
    return null;
}
/**
 * The kiosk's split of a normalised full name (mirrors `_splitVisitorName` in the kiosk):
 * at the LAST space, "first" = everything before it (middle names included). One word in a
 * spaceless script (≥2 code points) splits family-name-first: last = its first code point.
 * `null` when the value does not name both parts.
 */
export function splitFullName(normalised) {
    const lastSpace = normalised.lastIndexOf(' ');
    if (lastSpace !== -1) {
        return { firstName: normalised.slice(0, lastSpace), lastName: normalised.slice(lastSpace + 1) };
    }
    const chars = Array.from(normalised);
    if (chars.length >= 2 && chars.some((ch) => isSpacelessScript(ch.codePointAt(0) ?? 0))) {
        return { lastName: chars[0] ?? '', firstName: chars.slice(1).join('') };
    }
    return null;
}
/**
 * The pure policy check — no Zod. Normalises, then runs C1–C11 in order; the first failure
 * wins. On success, `value` is the normalised form, which is what must be stored.
 */
export function checkText(value, profile, options) {
    const multiLine = options.multiLine === true && allowsMultiLine(profile);
    const v = normalizeText(value, profile, multiLine);
    if (profile === 'searchText')
        return { ok: true, value: v };
    // C1
    if (v === '')
        return options.allowEmpty === true
            ? { ok: true, value: '' }
            : { ok: false, code: 'TEXT_REQUIRED' };
    const cps = codePoints(v);
    // C2–C6
    const base = checkBase(cps, profile, multiLine);
    if (base !== null)
        return { ok: false, code: base };
    const isName = profile === 'personName' || profile === 'kioskFullName';
    // C7
    if (isName && !cps.every(isNameChar))
        return { ok: false, code: 'NAME_INVALID' };
    if ((profile === 'label' || profile === 'freeText') &&
        !cps.every((cp) => isLabelChar(cp, multiLine))) {
        return { ok: false, code: 'TEXT_INVALID_CHARS' };
    }
    // C8
    if (profile === 'personName' && !cps.some(isLetter))
        return { ok: false, code: 'NAME_INVALID' };
    if (profile === 'kioskFullName' &&
        v.split(' ').some((word) => !codePoints(word).some(isLetter))) {
        return { ok: false, code: 'NAME_INVALID' };
    }
    // C9
    if (cps.length > options.max)
        return { ok: false, code: 'TEXT_TOO_LONG' };
    if (profile === 'kioskFullName') {
        // C10
        const parts = splitFullName(v);
        if (parts === null)
            return { ok: false, code: 'NAME_NEEDS_FIRST_AND_LAST' };
        // C11
        if (codePoints(parts.firstName).length > PERSON_NAME_PART_MAX_LENGTH ||
            codePoints(parts.lastName).length > PERSON_NAME_PART_MAX_LENGTH) {
            return { ok: false, code: 'TEXT_TOO_LONG' };
        }
    }
    return { ok: true, value: v };
}
function issueFor(code, max) {
    // REQUIRED / TOO_LONG keep Zod's own issue codes, so a form mapping `too_small` /
    // `too_big` (and the `maximum` it quotes) keeps working; `message` is always the policy code.
    if (code === 'TEXT_REQUIRED') {
        return {
            code: z.ZodIssueCode.too_small,
            minimum: 1,
            inclusive: true,
            exact: false,
            type: 'string',
            message: code,
        };
    }
    if (code === 'TEXT_TOO_LONG') {
        return {
            code: z.ZodIssueCode.too_big,
            maximum: max,
            inclusive: true,
            exact: false,
            type: 'string',
            message: code,
        };
    }
    return { code: z.ZodIssueCode.custom, message: code, params: { textPolicyCode: code } };
}
function textPolicySchema(profile, options) {
    const schema = z.string().transform((raw, ctx) => {
        const result = checkText(raw, profile, options);
        if (result.ok)
            return result.value;
        ctx.addIssue(issueFor(result.code, options.max));
        return z.NEVER;
    });
    return Object.assign(schema, { maxLength: options.max, textProfile: profile });
}
/** First/last name — one part. `max` defaults to 100 code points. */
export function personName(options = {}) {
    return textPolicySchema('personName', { max: PERSON_NAME_PART_MAX_LENGTH, ...options });
}
/** Company, job title, department, location/device/printer names, address lines. */
export function label(options) {
    return textPolicySchema('label', options);
}
/** Purpose, notes, TEXT answers. `multiLine` permits `\n`. */
export function freeText(options) {
    return textPolicySchema('freeText', options);
}
/** Admin-authored display copy: base rules 1–7, emoji allowed, no character set. */
export function displayCopy(options) {
    return textPolicySchema('displayCopy', options);
}
/** The kiosk's single full-name field (§1d). `max` defaults to 201. */
export function kioskFullName(options = {}) {
    return textPolicySchema('kioskFullName', { max: KIOSK_FULL_NAME_MAX_LENGTH, ...options });
}
/** Search input: NFC + trim, never rejected — an unmatched query just returns nothing. */
export function searchText() {
    return z.string().transform((raw) => normalizeText(raw, 'searchText'));
}
//# sourceMappingURL=text-policy.js.map